/**  @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2, breakpoints } from '@coursera/cds-core';

import _t from 'i18n!nls/video-player';

const styles = {
  videoTimeDisplay: css`
    height: 100%;
    margin-right: auto;
    margin-left: 10px;
    color: #dedede;

    ${breakpoints.down('xs')} {
      height: 30px;
      display: flex;
      align-items: center;
    }
  `,
  modernVideoTimeDisplay: css`
    margin-left: var(--cds-spacing-200);
    margin-right: 0;
    height: auto;
  `,
  currentTimeDisplay: css`
    font-size: 14px;
  `,
  timeSeparator: css`
    font-size: 14px;
    margin: 0 8px;
  `,
  durationDisplay: css`
    font-size: 14px;
  `,
};

type Props = {
  player: any;
  trimmedStartTimeSeconds: number;
};

type State = {
  currentTime: number | null;
  duration: number | null;
};

class VideoTimeDisplay extends React.Component<Props, State> {
  state = {
    currentTime: null,
    duration: null,
  };

  componentDidMount() {
    const { player } = this.props;
    this.setState({
      currentTime: this.getCurrentTime(),
      duration: player.duration(),
    });
    player.on('timeupdate', this.onTimeChange);
    player.on('durationchange', this.onDurationChange);
  }

  componentWillUnmount() {
    const { player } = this.props;
    player.off('timeupdate', this.onTimeChange);
    player.off('durationchange', this.onDurationChange);
  }

  getCurrentTime = () => {
    const { trimmedStartTimeSeconds, player } = this.props;
    return player.currentTime() - trimmedStartTimeSeconds;
  };

  onTimeChange = () => {
    this.setState({
      currentTime: this.getCurrentTime(),
    });
  };

  onDurationChange = () => {
    const { player } = this.props;
    this.setState({
      duration: player.duration(),
    });
  };

  convertSecondsToDisplay = (timeInSeconds: number | null, showSiSymbolForA11y?: boolean): string => {
    if (!timeInSeconds || timeInSeconds < 0) {
      if (showSiSymbolForA11y) {
        return '0 minutes 0 seconds';
      }

      return `0:00`;
    }

    const minuteDisplay = `${Math.floor(timeInSeconds / 60)}`;
    const secondsDisplay = `${Math.floor(timeInSeconds % 60)}`.padStart(2, '0');

    if (showSiSymbolForA11y) {
      return `${minuteDisplay} minutes ${secondsDisplay} seconds`;
    }

    return `${minuteDisplay}:${secondsDisplay}`;
  };

  render() {
    const { currentTime, duration } = this.state;
    // don't render in case state hasn't initialized correctly
    if (currentTime === null || currentTime === undefined || duration === null || duration === undefined) {
      return null;
    }

    return (
      <React.Fragment>
        <div css={[styles.videoTimeDisplay, styles.modernVideoTimeDisplay]}>
          <Typography2 component="span" variant="subtitleLarge" color="invertBody">
            <span className="current-time-display" aria-label={_t(this.convertSecondsToDisplay(currentTime, true))}>
              {this.convertSecondsToDisplay(currentTime)}
            </span>
            <span className="time-separator"> / </span>
            <span className="duration-display" aria-label={_t(this.convertSecondsToDisplay(duration, true))}>
              {this.convertSecondsToDisplay(duration)}
            </span>
          </Typography2>
        </div>
      </React.Fragment>
    );
  }
}

export default VideoTimeDisplay;
